import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, FileText, ChevronRight } from 'lucide-react';
import { cn, Button, Paginator } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAdminEpreuves } from '@/lib/admin/epreuve-queries';
import type { AdminEpreuveSummary } from '@/lib/admin/epreuve-queries';
import { useUpdateEpreuve } from '@/lib/admin/epreuve-mutations';
import { useRouterState } from '@tanstack/react-router';
import { TutorLibraryTabs } from '@/components/tutor-library-tabs';
import { usePagedList } from '@/lib/use-paged-list';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const SUBJECT_EMOJI = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.emoji]));

const EXAM_TYPE_LABEL: Record<string, string> = {
  brevet: 'Brevet',
  bac: 'Bac',
  bac_pro: 'Bac Pro',
  controle_commun: 'Contrôle commun',
  ds: 'DS',
  autre: 'Autre',
};

const STATUS_STYLES: Record<string, string> = {
  published: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
};

const STATUS_LABEL: Record<string, string> = {
  published: 'Publiée',
  draft: 'Brouillon',
  archived: 'Archivée',
};

function EpreuveRow({ epreuve }: { epreuve: AdminEpreuveSummary }) {
  const update = useUpdateEpreuve();

  function toggleStatus() {
    const next = epreuve.status === 'published' ? 'draft' : 'published';
    update.mutate({ id: epreuve.id, status: next });
  }

  return (
    <div className="hover:bg-ink-50 group flex items-center gap-3 rounded-xl p-3 transition-colors">
      <Link
        to="/tutor/admin/epreuves/$epreuveId"
        params={{ epreuveId: epreuve.id }}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <div className="bg-level-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
          <FileText className="text-level-500 h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
            {epreuve.title}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-ink-500 font-sans text-[11px]">
              {LEVEL_LABEL[epreuve.level] ?? epreuve.level}
            </span>
            {epreuve.subject && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-ink-500 font-sans text-[11px]">
                  {SUBJECT_LABEL[epreuve.subject] ?? epreuve.subject}
                </span>
              </>
            )}
            {epreuve.examType && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-ink-500 font-sans text-[11px]">
                  {EXAM_TYPE_LABEL[epreuve.examType] ?? epreuve.examType}
                  {epreuve.examYear ? ` ${epreuve.examYear}` : ''}
                </span>
              </>
            )}
            <span className="text-ink-300">·</span>
            <span className="text-ink-500 font-sans text-[11px]">
              {epreuve.exercise_count} exercice{epreuve.exercise_count !== 1 ? 's' : ''}
            </span>
            {epreuve.ocr_status === 'processing' && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-level-600 font-sans text-[10px] font-semibold">
                  Traitement PDF…
                </span>
              </>
            )}
          </div>
        </div>
        <span
          className={cn(
            'font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            STATUS_STYLES[epreuve.status] ?? 'bg-ink-100 text-ink-600',
          )}
        >
          {STATUS_LABEL[epreuve.status] ?? epreuve.status}
        </span>
        <ChevronRight className="text-ink-300 group-hover:text-ink-500 h-4 w-4 shrink-0 transition-colors" />
      </Link>
      <button
        onClick={toggleStatus}
        disabled={update.isPending}
        className="text-primary-600 hover:text-primary-800 shrink-0 font-sans text-[11px] underline transition-opacity disabled:opacity-50"
      >
        {epreuve.status === 'published' ? 'Dépublier' : 'Publier'}
      </button>
    </div>
  );
}

type GroupEntry = {
  subject: SubjectSlug | null;
  level: SchoolLevel;
  epreuves: AdminEpreuveSummary[];
};

function buildGroups(epreuves: AdminEpreuveSummary[]): GroupEntry[] {
  const map = new Map<string, GroupEntry>();

  for (const e of epreuves) {
    const key = `${e.subject ?? '_none'}|${e.level}`;
    if (!map.has(key)) {
      map.set(key, { subject: e.subject, level: e.level, epreuves: [] });
    }
    map.get(key)!.epreuves.push(e);
  }

  const subjectOrder = [...SUBJECTS.map((s) => s.value), null];
  const levelOrder = SCHOOL_LEVELS.map((l) => l.value);

  return [...map.values()].sort((a, b) => {
    const si = subjectOrder.indexOf(a.subject as SubjectSlug);
    const sj = subjectOrder.indexOf(b.subject as SubjectSlug);
    if (si !== sj) return si - sj;
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });
}

export function TutorAdminEpreuvesPage() {
  const { data: epreuves = [], isLoading } = useAdminEpreuves();
  const [filterSubject, setFilterSubject] = useState<SubjectSlug | 'all'>('all');
  const [filterLevel, setFilterLevel] = useState<SchoolLevel | 'all'>('all');

  const availableSubjects = useMemo(
    () => [...new Set(epreuves.map((e) => e.subject).filter(Boolean) as SubjectSlug[])],
    [epreuves],
  );
  const availableLevels = useMemo(
    () => SCHOOL_LEVELS.map((l) => l.value).filter((v) => epreuves.some((e) => e.level === v)),
    [epreuves],
  );

  const filtered = useMemo(() => {
    return epreuves.filter((e) => {
      if (filterSubject !== 'all' && e.subject !== filterSubject) return false;
      if (filterLevel !== 'all' && e.level !== filterLevel) return false;
      return true;
    });
  }, [epreuves, filterSubject, filterLevel]);

  const groups = useMemo(() => buildGroups(filtered), [filtered]);
  // Pagination (20 épreuves par page) dans l'ordre matière → niveau.
  const allGroups = groups;
  const groupTotals = new Map(allGroups.map((g) => [`${g.subject}|${g.level}`, g.epreuves.length]));
  const { items: pageEpreuves, paginator } = usePagedList(
    allGroups.flatMap((g) => g.epreuves),
    { resetKeys: [filterSubject, filterLevel], unit: 'épreuves', label: 'Pages des épreuves' },
  );
  const pageGroups = buildGroups(pageEpreuves);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showLibraryTabs = pathname === '/tutor/epreuves' || pathname.startsWith('/tutor/epreuves/');

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {showLibraryTabs && <TutorLibraryTabs />}
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Épreuves</h1>
          <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
            {filtered.length} épreuve{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/tutor/admin/epreuves/new">
          <Button size="sm" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nouvelle épreuve</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </Link>
      </div>

      {/* Filters */}
      {!isLoading && epreuves.length > 0 && (
        <div className="mb-5 space-y-2">
          {availableSubjects.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <FilterPill active={filterSubject === 'all'} onClick={() => setFilterSubject('all')}>
                Toutes matières
              </FilterPill>
              {availableSubjects.map((s) => (
                <FilterPill
                  key={s}
                  active={filterSubject === s}
                  onClick={() => setFilterSubject(filterSubject === s ? 'all' : s)}
                >
                  {SUBJECT_EMOJI[s]} {SUBJECT_LABEL[s] ?? s}
                </FilterPill>
              ))}
            </div>
          )}
          {availableLevels.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <FilterPill active={filterLevel === 'all'} onClick={() => setFilterLevel('all')}>
                Tous niveaux
              </FilterPill>
              {availableLevels.map((l) => (
                <FilterPill
                  key={l}
                  active={filterLevel === l}
                  onClick={() => setFilterLevel(filterLevel === l ? 'all' : l)}
                >
                  {LEVEL_LABEL[l] ?? l}
                </FilterPill>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <FileText className="text-ink-300 mx-auto mb-3 h-10 w-10" />
          <p className="font-display text-ink-600 font-semibold">Aucune épreuve</p>
          <p className="text-ink-400 mt-1 font-sans text-[13px]">
            {epreuves.length > 0
              ? 'Aucune épreuve ne correspond aux filtres sélectionnés.'
              : 'Créez votre première épreuve depuis un PDF ou manuellement.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {pageGroups.map((group) => {
            const subjectLabel = group.subject
              ? `${SUBJECT_EMOJI[group.subject] ?? ''} ${SUBJECT_LABEL[group.subject] ?? group.subject}`
              : 'Sans matière';
            const levelLabel = LEVEL_LABEL[group.level] ?? group.level;
            return (
              <div key={`${group.subject}|${group.level}`}>
                <h2 className="font-display text-ink-600 mb-2 px-1 text-[12px] font-semibold uppercase tracking-wide">
                  {subjectLabel} — {levelLabel}
                  <span className="text-ink-400 ml-1.5 font-sans font-normal normal-case">
                    ({groupTotals.get(`${group.subject}|${group.level}`) ?? group.epreuves.length})
                  </span>
                </h2>
                <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
                  {group.epreuves.map((e) => (
                    <EpreuveRow key={e.id} epreuve={e} />
                  ))}
                </div>
              </div>
            );
          })}
          <Paginator {...paginator} />
        </div>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'font-display min-h-[2rem] rounded-full px-3 py-1 text-[12px] font-semibold transition-colors active:scale-[0.98]',
        active ? 'bg-primary-500 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
      )}
    >
      {children}
    </button>
  );
}
